import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Clock,
  FileEdit,
  Clipboard,
  Terminal,
  Play,
  Eye,
  AlertTriangle,
  Code,
  File,
  Save,
  Activity,
  RefreshCw,
  Keyboard,
  Copy,
  Scissors,
  ClipboardPaste,
  FolderOpen,
  FolderX,
  BarChart3,
  Filter,
  Plus,
  Minus,
  Bug,
  CheckCircle2,
  XCircle,
  Focus,
  Globe,
  Network
} from 'lucide-react';
import { activity, labs } from '../services/api';
import { format } from 'date-fns';
import { clipboardOperationLabel, clipboardEvidenceNote } from '../utils/clipboardEvidence';

// Event type configurations with icons and colors
const EVENT_CONFIG = {
  document_edit: {
    icon: FileEdit,
    color: 'var(--accent-primary)',
    label: 'Edit',
    bgColor: 'var(--badge-info-bg)'
  },
  file_saved: {
    icon: Save,
    color: 'var(--accent-success)',
    label: 'Save',
    bgColor: 'var(--badge-success-bg)'
  },
  file_opened: {
    icon: FolderOpen,
    color: 'var(--accent-purple)',
    label: 'Open',
    bgColor: 'var(--badge-info-bg)'
  },
  file_closed: {
    icon: FolderX,
    color: 'var(--text-secondary)',
    label: 'Close',
    bgColor: 'var(--bg-tertiary)'
  },
  active_file_changed: {
    icon: Focus,
    color: 'var(--accent-warning)',
    label: 'Focus',
    bgColor: 'var(--badge-warning-bg)'
  },
  clipboard: {
    icon: Clipboard,
    color: 'var(--accent-danger)',
    label: 'Clipboard',
    bgColor: 'var(--badge-danger-bg)'
  },
  text_selected: {
    icon: Eye,
    color: 'var(--accent-cyan)',
    label: 'Select',
    bgColor: 'var(--badge-info-bg)'
  },
  keystrokes_batch: {
    icon: Keyboard,
    color: 'var(--accent-primary)',
    label: 'Typing',
    bgColor: 'var(--badge-info-bg)'
  },
  command_executed: {
    icon: Terminal,
    color: 'var(--accent-purple)',
    label: 'Command',
    bgColor: 'var(--badge-info-bg)'
  },
  terminal_opened: {
    icon: Terminal,
    color: 'var(--accent-success)',
    label: 'Terminal Opened',
    bgColor: 'var(--badge-success-bg)'
  },
  terminal_closed: {
    icon: Terminal,
    color: 'var(--text-secondary)',
    label: 'Terminal Closed',
    bgColor: 'var(--bg-tertiary)'
  },
  terminal_focus_changed: {
    icon: Terminal,
    color: 'var(--accent-warning)',
    label: 'Terminal Focus',
    bgColor: 'var(--badge-warning-bg)'
  },
  code_runner_started: {
    icon: Play,
    color: 'var(--accent-primary)',
    label: 'Run Code',
    bgColor: 'var(--badge-info-bg)'
  },
  code_runner_completed: {
    icon: CheckCircle2,
    color: 'var(--accent-success)',
    label: 'Run Complete',
    bgColor: 'var(--badge-success-bg)'
  },
  debug_started: {
    icon: Bug,
    color: 'var(--accent-danger)',
    label: 'Debug',
    bgColor: 'var(--badge-danger-bg)'
  },
  debug_ended: {
    icon: Bug,
    color: 'var(--text-secondary)',
    label: 'Debug',
    bgColor: 'var(--bg-tertiary)'
  },
  task_started: {
    icon: Play,
    color: 'var(--accent-success)',
    label: 'Task',
    bgColor: 'var(--badge-success-bg)'
  },
  task_ended: {
    icon: CheckCircle2,
    color: 'var(--text-secondary)',
    label: 'Task',
    bgColor: 'var(--bg-tertiary)'
  },
  session_started: {
    icon: CheckCircle2,
    color: 'var(--accent-success)',
    label: 'Session',
    bgColor: 'var(--badge-success-bg)'
  },
  session_ended: {
    icon: XCircle,
    color: 'var(--accent-danger)',
    label: 'Session',
    bgColor: 'var(--badge-danger-bg)'
  },
  window_focus_changed: {
    icon: Eye,
    color: 'var(--accent-warning)',
    label: 'Window',
    bgColor: 'var(--badge-warning-bg)'
  },
  d2l_session_reminder: {
    icon: Clock,
    color: 'var(--text-secondary)',
    label: 'D2L Reminder',
    bgColor: 'var(--bg-tertiary)'
  },
  file_change: {
    icon: FileEdit,
    color: 'var(--accent-primary)',
    label: 'File System',
    bgColor: 'var(--badge-info-bg)'
  },
  default: {
    icon: Code,
    color: 'var(--text-secondary)',
    label: 'Event',
    bgColor: 'var(--bg-tertiary)'
  }
};

// Category filters
const CATEGORIES = [
  { id: 'all', label: 'All', icon: Activity },
  { id: 'editing', label: 'Editing', icon: FileEdit },
  { id: 'navigation', label: 'Navigation', icon: FolderOpen },
  { id: 'clipboard', label: 'Clipboard', icon: Clipboard },
  { id: 'terminal', label: 'Terminal', icon: Terminal },
];

function StudentActivity() {
  const { labId, studentId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch lab info
  const { data: lab } = useQuery({
    queryKey: ['lab', labId],
    queryFn: () => labs.get(labId),
  });

  // Fetch activity events
  const { data: eventsData, isLoading: eventsLoading, refetch } = useQuery({
    queryKey: ['activity-events', labId, studentId, selectedCategory],
    queryFn: () => activity.getEvents(labId, studentId, {
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      limit: 5000
    }),
    refetchInterval: 10000,
  });

  // Fetch activity summary
  const { data: summaryData } = useQuery({
    queryKey: ['activity-summary', labId, studentId],
    queryFn: () => activity.getSummary(labId, studentId),
    refetchInterval: 30000,
  });

  const events = eventsData?.events || [];
  const summary = summaryData || {};

  // Group events chronologically by time buckets
  const groupedEvents = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    let lastTimestamp = null;

    // Sort events by timestamp (newest first)
    const sortedEvents = [...events].sort((a, b) => 
      new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );

    for (const event of sortedEvents) {
      if (!event.timestamp) continue;
      
      const eventTime = new Date(event.timestamp);
      
      // Create new group if more than 2 minutes gap or different minute
      if (!currentGroup || !lastTimestamp || 
          (lastTimestamp - eventTime) > 120000 || // 2 minutes
          lastTimestamp.getMinutes() !== eventTime.getMinutes()) {
        currentGroup = {
          timestamp: event.timestamp,
          events: []
        };
        groups.push(currentGroup);
      }
      
      currentGroup.events.push(event);
      lastTimestamp = eventTime;
    }
    
    return groups;
  }, [events]);

  const student = lab?.students?.find(s => s.student_id === studentId);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Link 
          to={`/labs/${labId}`}
          className="btn btn-secondary btn-sm"
          style={{ marginBottom: 12 }}
        >
          <ArrowLeft size={16} />
          Back to {lab?.display_name || 'Lab'}
        </Link>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Activity size={28} />
              {student?.student_name || studentId}
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              Activity Timeline • {events.length} events recorded
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <a 
              href={student?.container?.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                opacity: student?.container?.url ? 1 : 0.5,
                pointerEvents: student?.container?.url ? 'auto' : 'none'
              }}
            >
              <Code size={16} />
              Open IDE
            </a>
            <button 
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={eventsLoading}
            >
              <RefreshCw size={16} className={eventsLoading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats - Compact */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 8,
        marginBottom: 16,
      }}>
        <StatBadge
          label="Edits"
          value={summary.events_by_type?.document_edit || 0}
          icon={FileEdit}
          color="var(--accent-primary)"
        />
        <StatBadge
          label="Saves"
          value={summary.events_by_type?.file_saved || 0}
          icon={Save}
          color="var(--accent-success)"
        />
        <StatBadge
          label="Clipboard"
          value={summary.clipboard_operations_count || 0}
          icon={Clipboard}
          color="var(--accent-danger)"
          warning={summary.clipboard_operations_count > 10}
        />
        <StatBadge
          label="Terminal"
          value={(summary.events_by_type?.terminal_opened || 0) +
                (summary.events_by_type?.terminal_closed || 0)}
          icon={Terminal}
          color="var(--accent-success)"
        />
        <StatBadge
          label="Debug"
          value={(summary.events_by_type?.debug_started || 0)}
          icon={Bug}
          color="var(--accent-danger)"
        />
        <StatBadge
          label="Duration"
          value={summary.session_duration_seconds
            ? `${Math.round(summary.session_duration_seconds / 60)}m`
            : '0m'
          }
          icon={Clock}
          color="var(--accent-purple)"
        />
        <StatBadge
          label="IPs Used"
          value={summary.network_info?.remote_ips_count || 0}
          icon={Globe}
          color="var(--accent-warning)"
          warning={summary.network_info?.remote_ips_count > 1}
        />
      </div>

      {/* Network Information - Detailed */}
      {summary.network_info && (
        <div style={{
          padding: 12,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8, 
            marginBottom: 10,
            fontWeight: 500,
            color: 'var(--text-primary)'
          }}>
            <Network size={16} />
            <span>Network Information</span>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {summary.network_info.local_ip && summary.network_info.local_ip !== 'unknown' && (
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Local IP:</span>
                <code style={{ 
                  marginLeft: 8, 
                  padding: '2px 6px', 
                  background: 'var(--bg-primary)', 
                  borderRadius: 4,
                  fontSize: 12
                }}>
                  {summary.network_info.local_ip}
                </code>
              </div>
            )}
            {summary.network_info.public_ip && summary.network_info.public_ip !== 'unknown' && (
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Public IP:</span>
                <code style={{ 
                  marginLeft: 8, 
                  padding: '2px 6px', 
                  background: 'var(--bg-primary)', 
                  borderRadius: 4,
                  fontSize: 12
                }}>
                  {summary.network_info.public_ip}
                </code>
              </div>
            )}
            {summary.network_info.remote_ips && summary.network_info.remote_ips.length > 0 && (
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Client IPs ({summary.network_info.remote_ips.length}):</span>
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {summary.network_info.remote_ips.map((ip, idx) => (
                    <code key={idx} style={{
                      padding: '2px 6px',
                      background: summary.network_info.remote_ips.length > 1
                        ? 'var(--badge-danger-bg)'
                        : 'var(--bg-primary)',
                      borderRadius: 4,
                      fontSize: 12,
                      border: summary.network_info.remote_ips.length > 1
                        ? '1px solid var(--accent-danger)'
                        : 'none'
                    }}>
                      {ip}
                    </code>
                  ))}
                </div>
                {summary.network_info.remote_ips.length > 1 && (
                  <div style={{
                    marginTop: 6,
                    padding: 6,
                    background: 'var(--btn-danger-bg)',
                    border: '1px solid var(--accent-danger)',
                    borderRadius: 4,
                    fontSize: 12,
                    color: 'var(--accent-danger)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    <AlertTriangle size={14} />
                    <span>Multiple IP addresses detected - possible shared account or VPN usage</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Filters - Compact Pills */}
      <div style={{ 
        display: 'flex', 
        gap: 6,
        marginBottom: 16,
        flexWrap: 'wrap',
      }}>
        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          const isActive = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                fontSize: 13,
                border: `1px solid ${isActive ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                background: isActive ? 'var(--accent-primary)' : 'transparent',
                color: isActive ? 'white' : 'var(--text-primary)',
                borderRadius: 20,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={14} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="card" style={{ padding: 0 }}>
        {eventsLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
              Loading activity...
            </p>
          </div>
        ) : groupedEvents.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Activity size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p>No activity events recorded yet</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>
              Events will appear here as the student works in their IDE
            </p>
          </div>
        ) : (
          <div style={{ padding: 12 }}>
            {groupedEvents.map((group, idx) => (
              <TimelineGroup key={idx} group={group} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact stat badge
function StatBadge({ label, value, icon: Icon, color, warning }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: 'var(--bg-secondary)',
      border: `1px solid ${warning ? 'var(--accent-danger)' : 'var(--border-primary)'}`,
      borderRadius: 6,
    }}>
      <Icon size={16} style={{ color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ 
          fontSize: 18, 
          fontWeight: 600, 
          fontFamily: 'var(--font-mono)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}>
          {value}
          {warning && <AlertTriangle size={12} style={{ color: 'var(--accent-danger)' }} />}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

// Timeline group component (events within same time window)
function TimelineGroup({ group }) {
  const timestamp = new Date(group.timestamp);
  const timeStr = format(timestamp, 'HH:mm:ss');
  const dateStr = format(timestamp, 'MMM d, yyyy');

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Time Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        paddingLeft: 4,
      }}>
        <Clock size={14} style={{ color: 'var(--text-muted)' }} />
        <span style={{ 
          fontSize: 12, 
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-secondary)',
          fontWeight: 500,
        }}>
          {dateStr} • {timeStr}
        </span>
        <span style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          background: 'var(--bg-tertiary)',
          padding: '2px 8px',
          borderRadius: 10,
        }}>
          {group.events.length} {group.events.length === 1 ? 'event' : 'events'}
        </span>
      </div>

      {/* Events in this group - Compact horizontal layout */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingLeft: 20,
        borderLeft: '2px solid var(--border-secondary)',
      }}>
        {group.events.map((event, idx) => (
          <EventItem key={event.seq || idx} event={event} />
        ))}
      </div>
    </div>
  );
}

// Compact event item
function EventItem({ event }) {
  const [expanded, setExpanded] = useState(false);
  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.default;
  
  // Get specific icon for clipboard operations
  let Icon = config.icon;
  if (event.type === 'clipboard') {
    if (event.operation === 'copy') Icon = Copy;
    else if (event.operation === 'cut') Icon = Scissors;
    else if (event.operation === 'paste') Icon = ClipboardPaste;
  }

  const description = getEventDescription(event);
  const hasDetails = shouldShowDetails(event);

  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={() => hasDetails && setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: config.bgColor,
          borderLeft: `3px solid ${config.color}`,
          borderRadius: 4,
          cursor: hasDetails ? 'pointer' : 'default',
          transition: 'all 0.15s',
        }}
      >
        <Icon size={14} style={{ color: config.color, flexShrink: 0 }} />
        
        <div style={{ 
          flex: 1, 
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}>
          <span style={{ fontWeight: 500, color: config.color, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            {config.label}
          </span>
          <span style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {description}
          </span>
        </div>

        {hasDetails && (
          <span style={{ 
            fontSize: 11, 
            color: 'var(--text-muted)',
            flexShrink: 0,
          }}>
            {expanded ? '▼' : '▶'}
          </span>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && hasDetails && (
        <EventDetails event={event} config={config} />
      )}
    </div>
  );
}

// Command details component with output preview
function CommandDetails({ event, config }) {
  const [showFullOutput, setShowFullOutput] = useState(false);
  
  const outputLines = event.output ? event.output.split('\n') : [];
  const hasLongOutput = outputLines.length > 10;
  const displayedOutput = showFullOutput ? event.output : outputLines.slice(0, 10).join('\n');
  
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <strong style={{ color: config.color }}>Command:</strong>
      </div>
      <pre style={{
        margin: 0,
        padding: 8,
        background: 'var(--bg-primary)',
        borderRadius: 4,
        fontSize: 11,
        fontFamily: 'var(--font-mono)',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
      }}>
        {event.command || '(no command captured)'}
      </pre>
      
      {event.working_directory && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          <strong>Directory:</strong> <code>{event.working_directory}</code>
        </div>
      )}
      
      {event.terminal && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          <strong>Terminal:</strong> 
          <code style={{ marginLeft: 6 }}>
            {event.terminal.number !== undefined && event.terminal.number !== '' 
              ? `Terminal ${event.terminal.number}` 
              : 'Terminal'}
            {event.terminal.tty && event.terminal.tty !== 'unknown' && (
              <span style={{ opacity: 0.6, marginLeft: 4 }}>({event.terminal.tty})</span>
            )}
          </code>
        </div>
      )}
      
      {event.exit_code !== undefined && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
          <strong>Exit Code:</strong> <code style={{ 
            color: event.exit_code === 0 ? 'var(--accent-success)' : 'var(--accent-danger)',
            fontWeight: 600 
          }}>{event.exit_code}</code>
        </div>
      )}
      
      {event.output && event.output.trim() && (
        <div style={{ marginTop: 12 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 8 
          }}>
            <strong style={{ color: config.color }}>Output:</strong>
            {hasLongOutput && (
              <button 
                onClick={() => setShowFullOutput(!showFullOutput)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: config.color,
                  cursor: 'pointer',
                  fontSize: 11,
                  padding: '2px 6px',
                  borderRadius: 3,
                  textDecoration: 'underline'
                }}
              >
                {showFullOutput ? 'Show less' : `Show all (${outputLines.length} lines)`}
              </button>
            )}
          </div>
          <pre style={{
            margin: 0,
            padding: 8,
            background: 'var(--bg-primary)',
            borderRadius: 4,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            maxHeight: showFullOutput ? 'none' : '200px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            border: '1px solid var(--border-secondary)',
            color: 'var(--text-primary)'
          }}>
            {displayedOutput}
            {hasLongOutput && !showFullOutput && (
              <div style={{ 
                marginTop: 8, 
                color: 'var(--text-muted)',
                fontStyle: 'italic' 
              }}>
                ... ({outputLines.length - 10} more lines)
              </div>
            )}
          </pre>
        </div>
      )}
    </div>
  );
}

// Event details (shown when expanded)
function EventDetails({ event, config }) {
  return (
    <div style={{
      marginTop: 4,
      marginLeft: 20,
      padding: 12,
      background: 'var(--bg-tertiary)',
      borderLeft: `2px solid ${config.color}`,
      borderRadius: 4,
      fontSize: 12,
    }}>
      {/* Document Edit - Show diff-like view */}
      {event.type === 'document_edit' && event.changes && (
        <div>
          <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Changes to {event.file}
          </div>
          {event.changes.map((change, idx) => (
            <div key={idx} style={{ 
              marginBottom: 6,
              padding: 8,
              background: 'var(--bg-primary)',
              borderRadius: 4,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
            }}>
              <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
                Line {change.range.startLine + 1}
                {change.range.endLine !== change.range.startLine && ` - ${change.range.endLine + 1}`}
              </div>
              
              {change.rangeLength > 0 && (
                <div style={{
                  color: 'var(--accent-danger)',
                  background: 'var(--btn-danger-bg)',
                  padding: 4,
                  marginBottom: 2,
                  borderRadius: 2,
                  wordBreak: 'break-all',
                  maxHeight: 100,
                  overflow: 'auto',
                }}>
                  <Minus size={10} style={{ display: 'inline', marginRight: 4 }} />
                  {change.deletedText ? (
                    <span>
                      {change.deletedText.length > 200 
                        ? change.deletedText.substring(0, 200) + '...' 
                        : change.deletedText}
                    </span>
                  ) : (
                    <span style={{ fontStyle: 'italic' }}>Deleted {change.rangeLength} chars</span>
                  )}
                </div>
              )}
              
              {change.text && change.text.trim() && (
                <div style={{
                  color: 'var(--accent-success)',
                  background: 'var(--btn-success-bg)',
                  padding: 4,
                  borderRadius: 2,
                  wordBreak: 'break-all',
                  maxHeight: 100,
                  overflow: 'auto',
                }}>
                  <Plus size={10} style={{ display: 'inline', marginRight: 4 }} />
                  {change.text.length > 100 
                    ? change.text.substring(0, 100) + '...' 
                    : change.text}
                </div>
              )}
            </div>
          ))}
          
          {event.isPaste && (
            <div style={{
              marginTop: 8,
              padding: 6,
              background: 'var(--btn-danger-bg)',
              borderRadius: 4,
              color: 'var(--accent-danger)',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <AlertTriangle size={12} />
              <strong>Possible paste operation detected</strong>
            </div>
          )}
        </div>
      )}

      {/* Clipboard - Show content preview */}
      {event.type === 'clipboard' && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <strong>{clipboardOperationLabel(event).toUpperCase()}</strong>
            {event.file && <span style={{ color: 'var(--text-muted)' }}> from {event.file}</span>}
          </div>
          {clipboardEvidenceNote(event) && (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>
              {clipboardEvidenceNote(event)}
            </p>
          )}
          {(event.text || event.content_preview || event.content) && (
            <pre style={{
              margin: 0,
              padding: 8,
              background: 'var(--bg-primary)',
              borderRadius: 4,
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              maxHeight: 150,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {event.text || event.content_preview || event.content}
            </pre>
          )}
          {event.content_length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
              {event.content_length} characters
            </div>
          )}
        </div>
      )}

      {/* Keystrokes - Show typing pattern */}
      {event.type === 'keystrokes_batch' && event.keys && (
        <div>
          <div style={{ marginBottom: 8 }}>
            <strong>{event.count || event.keys.length} keystrokes</strong>
          </div>
          
          {/* Show actual typed text */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
              <strong>Typed:</strong>
            </div>
            <pre style={{
              margin: 0,
              padding: 8,
              background: 'var(--bg-primary)',
              borderRadius: 4,
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              maxHeight: 100,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              color: config.color
            }}>
              {event.keys.map(k => k.key).join('')}
            </pre>
          </div>
          
          <div style={{ 
            fontSize: 11, 
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-muted)',
          }}>
            Files edited: {[...new Set(event.keys.map(k => k.filePath).filter(Boolean))].map(f => f.split('/').pop()).join(', ')}
          </div>
        </div>
      )}

      {/* Text Selection - Show selected content */}
      {event.type === 'text_selected' && (
        <div>
          <div style={{ marginBottom: 8 }}>
            Selected {event.selectedLength} characters in <code>{event.file}</code>
          </div>
          {event.selectedText && (
            <pre style={{
              margin: 0,
              padding: 8,
              background: 'var(--bg-primary)',
              borderRadius: 4,
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              maxHeight: 100,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}>
              {event.selectedText}
            </pre>
          )}
        </div>
      )}

      {/* File System Change */}
      {event.type === 'file_change' && (
        <div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4,
          }}>
            <span style={{
              padding: '2px 8px',
              background: event.event_type === 'file_created' ? 'var(--badge-success-bg)' :
                          event.event_type === 'file_deleted' ? 'var(--badge-danger-bg)' :
                          'var(--badge-info-bg)',
              color: event.event_type === 'file_created' ? 'var(--accent-success)' :
                     event.event_type === 'file_deleted' ? 'var(--accent-danger)' :
                     'var(--accent-primary)',
              borderRadius: 3,
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}>
              {event.event_type?.replace('file_', '') || 'changed'}
            </span>
          </div>
          <code style={{ fontSize: 11, wordBreak: 'break-all' }}>
            {event.file_path || event.file}
          </code>
        </div>
      )}

      {/* Debug/Task info */}
      {(event.type === 'debug_started' || event.type === 'task_started') && (
        <div>
          <div><strong>{event.session_name || event.task_name}</strong></div>
          {event.session_type && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Type: {event.session_type}
            </div>
          )}
          {event.configuration && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Config: {event.configuration}
            </div>
          )}
        </div>
      )}

      {/* Command Executed */}
      {event.type === 'command_executed' && (
        <CommandDetails event={event} config={config} />
      )}

      {/* Generic JSON fallback */}
      {!['document_edit', 'clipboard', 'keystrokes_batch', 'text_selected', 'file_change', 'debug_started', 'debug_ended', 'task_started', 'task_ended', 'command_executed'].includes(event.type) && (
        <pre style={{
          margin: 0,
          fontSize: 10,
          fontFamily: 'var(--font-mono)',
          maxHeight: 200,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}>
          {JSON.stringify(event, null, 2)}
        </pre>
      )}

      {/* Network information - Show IP addresses */}
      {(event.remote_ip || event.local_ip || event.public_ip) && (
        <div style={{
          marginTop: 12,
          paddingTop: 8,
          borderTop: '1px solid var(--border-primary)',
          fontSize: 11,
          color: 'var(--text-muted)',
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          {event.remote_ip && (
            <div>
              <Globe size={10} style={{ display: 'inline', marginRight: 4 }} />
              Client: <code style={{ fontSize: 10 }}>{event.remote_ip}</code>
            </div>
          )}
          {event.local_ip && event.local_ip !== 'unknown' && (
            <div>
              <Network size={10} style={{ display: 'inline', marginRight: 4 }} />
              Local: <code style={{ fontSize: 10 }}>{event.local_ip}</code>
            </div>
          )}
          {event.public_ip && event.public_ip !== 'unknown' && (
            <div>
              <Globe size={10} style={{ display: 'inline', marginRight: 4 }} />
              Public: <code style={{ fontSize: 10 }}>{event.public_ip}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Get human-readable event description
function getEventDescription(event) {
  switch (event.type) {
    case 'document_edit':
      const changes = event.changeCount || event.changes?.length || 1;
      return `${event.file || 'file'}${changes > 1 ? ` (${changes} changes)` : ''}`;
    case 'file_saved':
      return event.file || 'file';
    case 'file_opened':
      return event.file || 'file';
    case 'file_closed':
      return event.file || 'file';
    case 'active_file_changed':
      return event.newFile || 'file';
    case 'clipboard':
      return `${clipboardOperationLabel(event)} • ${event.content_length || 0} chars`;
    case 'text_selected':
      return `${event.selectedLength || 0} chars • ${event.file || 'file'}`;
    case 'keystrokes_batch':
      const keystrokeText = event.keys ? event.keys.map(k => k.key).join('') : '';
      const preview = keystrokeText.length > 20 ? keystrokeText.substring(0, 20) + '...' : keystrokeText;
      return preview || `${event.count || event.keys?.length || 0} keystrokes`;
    case 'command_executed':
      const terminalInfo = event.terminal?.number !== undefined && event.terminal?.number !== '' 
        ? ` [Terminal ${event.terminal.number}]` 
        : '';
      return (event.command || 'command') + terminalInfo;
    case 'terminal_opened':
      if (event.terminal_name === 'Code') {
        return 'Terminal Opened: Code Runner';
      }
      return 'Terminal Opened';
    case 'terminal_closed':
      if (event.terminal_name === 'Code') {
        return 'Terminal Closed: Code Runner';
      }
      return 'Terminal Closed';
    case 'terminal_focus_changed':
      if (event.terminal_name === 'Code') {
        return 'Terminal Focus: Code Runner';
      }
      return 'Terminal Focus Changed';
    case 'debug_started':
    case 'debug_ended':
      return event.session_name || event.configuration || 'debug session';
    case 'task_started':
    case 'task_ended':
      return event.task_name || 'task';
    case 'session_started':
      return 'IDE session started';
    case 'session_ended':
      return 'IDE session ended';
    case 'window_focus_changed':
      return event.focused ? 'Window focused' : 'Window blurred';
    case 'd2l_session_reminder':
      if (event.action === 'acknowledged') return 'D2L check acknowledged by student (not verified)';
      if (event.action === 'snoozed') return 'D2L reminder snoozed for 2 minutes';
      return 'D2L session reminder shown';
    case 'file_change':
      return `${event.event_type?.replace('file_', '') || 'changed'} • ${event.file_path || event.file || 'file'}`;
    default:
      return event.type?.replace(/_/g, ' ') || 'event';
  }
}

// Check if event should have expandable details
function shouldShowDetails(event) {
  const detailTypes = [
    'document_edit',
    'clipboard',
    'keystrokes_batch',
    'text_selected',
    'file_change',
    'debug_started',
    'task_started',
    'command_executed'
  ];
  return detailTypes.includes(event.type);
}

export default StudentActivity;
